// src/pages/SignupPage.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/authContext';
import { useNavigate, Link } from 'react-router-dom';
import { API as api } from '../services/api';

export default function SignupPage() {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setInfo(null);

    if (!email || !password || !password2) {
      setError('Please fill in all required fields.');
      return;
    }

    if (password !== password2) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      // Adjust endpoint/payload to match your backend
      const res = await api.post('/auth/signup', {
        email,
        password,
        display_name: displayName || undefined,
      });

      // Option 1: auto-login with entered credentials (works with your /auth/login)
      await login(email, password);
      setInfo(res.data?.message || 'Account created successfully.');
      navigate('/', { replace: true });
    } catch (err) {
      console.error('Signup error:', err);
      setError(
        err.response?.data?.message ||
        'Could not create account. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-brand">
            <div className="auth-brand-logo">
              <img
                src="/logo.png"
                alt="Logo"
                style={{ height: "4em", width: "auto", display: "inline-block" }}
              />
            </div>            <div className="auth-brand-text">YieldMap</div>
          </div>
          <h1 className="auth-title">Create an account</h1>
          <p className="auth-subtitle">
            Sign up to request access. An admin may need to approve you.
          </p>
        </div>

        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="input-group">
            <label className="input-label" htmlFor="displayName">
              Name (optional)
            </label>
            <input
              id="displayName"
              type="text"
              className="input-field"
              placeholder="Your name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              autoComplete="name"
            />
            <span className="input-helper">
              This will be shown in the application header.
            </span>
          </div>

          <div className="input-group">
            <label className="input-label" htmlFor="email">
              Work email
            </label>
            <input
              id="email"
              type="email"
              className="input-field"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </div>

          <div className="input-group">
            <label className="input-label" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              type="password"
              className="input-field"
              placeholder="Create a password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
          </div>

          <div className="input-group">
            <label className="input-label" htmlFor="password2">
              Confirm password
            </label>
            <input
              id="password2"
              type="password"
              className="input-field"
              placeholder="Repeat password"
              value={password2}
              onChange={(e) => setPassword2(e.target.value)}
              autoComplete="new-password"
              required
            />
          </div>

          {error && <div className="error-message">{error}</div>}
          {info && <div className="success-message">{info}</div>}

          <button
            type="submit"
            className="btn-primary"
            disabled={submitting}
            style={{ width: '100%', justifyContent: 'center', marginTop: '0.5rem' }}
          >
            {submitting ? 'Creating account…' : 'Sign up'}
          </button>
        </form>

        <div className="auth-footer">
          Already have an account?{' '}
          <Link to="/login" className="auth-link">
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
